import React, { useMemo } from 'react';
import styled from 'styled-components';

import BWButton from 'components/shared/BWButton';
import DataTable from 'components/shared/DataTable/index';
import useCopyToClipboard from 'hooks/useCopyToClipboard';

const SummaryList = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  min-height: 36px;
  color: #667085;

  strong {
    display: block;
    margin-top: 2px;
    font-size: 16px;
    line-height: 1;
    color: #101828;
  }

  .used strong {
    color: #d92d20;
  }

  .available strong {
    color: #079455;
  }
`;

const StatusTag = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 2px 8px;
  color: ${({ $tone }) =>
    $tone === 'success' ? '#067647' : $tone === 'danger' ? '#b42318' : '#475467'};
  background: ${({ $tone }) =>
    $tone === 'success' ? '#effcf4' : $tone === 'danger' ? '#fff3f3' : 'var(--whiteColor)'};
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'success' ? '#75e0a7' : $tone === 'danger' ? '#fda29b' : '#d0d5dd'};
  border-radius: 4px;
  font-size: 13px;
  white-space: nowrap;
`;

const PrimaryText = styled.strong`
  display: block;
  color: #101828;
  line-height: 1.35;
`;

const SecondaryText = styled.span`
  display: block;
  margin-top: 2px;
  color: #667085;
  font-size: 12px;
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

const EmptyText = () => <span className='bw_text_center'>--</span>;

const AutoGenTable = ({
  loading,
  loadingExport,
  data = [],
  totalPages,
  itemsPerPage,
  page,
  meta,
  totalItems,
  onChangePage,
  exportExcel,
}) => {
  const { copy } = useCopyToClipboard('Đã sao chép mã giảm giá');

  const columns = useMemo(
    () => [
      {
        header: 'Mã giảm giá',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_text_center',
        styleHeader: { minWidth: 190 },
        formatter: (item) => {
          const couponCode = String(item?.coupon_code || '').toUpperCase();
          return (
            <div className='bw_flex bw_align_items_center bw_justify_content_center' style={{ gap: 6 }}>
              <PrimaryText>{couponCode || '--'}</PrimaryText>
              <BWButton
                type='blue'
                table
                iconOnly
                icon='fi fi-rr-copy-alt'
                content='Sao chép mã'
                disabled={!couponCode}
                onClick={() => copy(couponCode)}
              />
            </div>
          );
        },
      },
      {
        header: 'Hiệu lực',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_text_center',
        styleHeader: { minWidth: 180 },
        formatter: (item) => (
          <div>
            <PrimaryText>{item?.start_date || '--'}</PrimaryText>
            <SecondaryText>đến {item?.end_date || 'không thời hạn'}</SecondaryText>
          </div>
        ),
      },
      {
        header: 'Trạng thái',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_text_center',
        formatter: (item) =>
          item?.is_used ? <StatusTag $tone='danger'>Đã sử dụng</StatusTag> : <StatusTag $tone='success'>Sẵn sàng</StatusTag>,
      },
      {
        header: 'Khách hàng',
        styleHeader: { minWidth: 210 },
        formatter: (item) => {
          if (!item?.customer_name && !item?.customer_phone && !item?.customer_email) return <EmptyText />;
          return (
            <div>
              <PrimaryText>{item?.customer_name || 'Khách hàng'}</PrimaryText>
              {item?.customer_phone ? <SecondaryText>{item.customer_phone}</SecondaryText> : null}
              {item?.customer_email ? <SecondaryText>{item.customer_email}</SecondaryText> : null}
            </div>
          );
        },
      },
      {
        header: 'Gửi mã',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_text_center',
        formatter: (item) => {
          if (item?.is_sent === 1 || item?.is_sent === true) return <StatusTag $tone='success'>Đã gửi</StatusTag>;
          if (item?.is_sent === 0 || item?.is_sent === false) return <StatusTag $tone='danger'>Gửi lỗi</StatusTag>;
          return <StatusTag>Chưa gửi</StatusTag>;
        },
      },
      {
        header: 'Thông tin sử dụng',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_text_center',
        styleHeader: { minWidth: 170 },
        formatter: (item) => {
          if (!item?.used_date && !item?.order_no) return <EmptyText />;
          return (
            <div>
              <PrimaryText>{item?.used_date || '--'}</PrimaryText>
              {item?.order_no ? <SecondaryText>Đơn hàng: {item.order_no}</SecondaryText> : null}
            </div>
          );
        },
      },
    ],
    [copy],
  );

  const actions = useMemo(
    () => [
      {
        globalAction: true,
        type: 'success',
        permission: 'SM_COUPON_EXCEL',
        icon: 'fi fi-rr-download',
        content: 'Xuất Excel',
        showContent: true,
        loading: loadingExport,
        disabled: loadingExport || !totalItems,
        onClick: exportExcel,
      },
    ],
    [exportExcel, loadingExport, totalItems],
  );

  const usedCount = Math.max(Number(meta?.total ?? totalItems ?? 0) - Number(meta?.non_used ?? 0), 0);
  const title = (
    <SummaryList>
      <div>
        Tổng số
        <strong>{Number(meta?.total ?? totalItems ?? 0).toLocaleString('vi-VN')}</strong>
      </div>
      <div className='used'>
        Đã sử dụng
        <strong>{usedCount.toLocaleString('vi-VN')}</strong>
      </div>
      <div className='available'>
        Sẵn sàng
        <strong>{Number(meta?.non_used ?? 0).toLocaleString('vi-VN')}</strong>
      </div>
    </SummaryList>
  );

  return (
    <DataTable
      title={title}
      loading={loading}
      columns={columns}
      actions={actions}
      data={data}
      totalPages={totalPages}
      itemsPerPage={itemsPerPage}
      page={page}
      totalItems={totalItems}
      onChangePage={onChangePage}
      noSelect
      hiddenDeleteClick
    />
  );
};

export default AutoGenTable;
